import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../auth/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faUser, faBuilding, faFileAlt, faUsers, faShoppingCart, faStore } from '@fortawesome/free-solid-svg-icons';
import { adminDb } from '../../api/adminSupabase';
import './Dashboard.css';

const AdminDashboard = () => {
    const { auth, userProfile } = useContext(AuthContext);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState({
        buyers: 0,
        sellers: 0,
        leads: 0,
        users: 0
    });
    const [recentLeads, setRecentLeads] = useState([]);

    // Get user's first name from profile or email
    const firstName = userProfile?.name?.split(' ')[0] || auth?.email?.split('@')[0] || "Admin";

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // Get statistics
                const { data: statsData, error: statsError } = await adminDb.getStats();
                if (statsError) throw new Error(statsError.message);
                setStats(statsData);
                
                // Get recent leads
                const { data: leadsData, error: leadsError } = await adminDb.getAllLeads();
                if (leadsError) throw new Error(leadsError.message);
                setRecentLeads(leadsData || []);
            } catch (err) {
                console.error('Error fetching admin data:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        
        fetchData();
    }, []);

    if (loading) {
        return (
            <div className="admin-dashboard loading">
                <div className="loading-spinner"></div>
                <p>Loading dashboard data...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-dashboard error">
                <h2>Error Loading Dashboard</h2>
                <p>{error}</p>
                <button onClick={() => window.location.reload()}>Try Again</button>
            </div>
        );
    }

    return (
        <div className="admin-dashboard">
            <h2>Welcome, {firstName}</h2>
            
            <div className="stats-cards">
                <div className="stat-card">
                    <FontAwesomeIcon icon={faUsers} className="stat-icon" />
                    <div className="stat-content">
                        <h3>Total Users</h3>
                        <p className="stat-value">{stats.users}</p>
                    </div>
                </div>
                <div className="stat-card">
                    <FontAwesomeIcon icon={faShoppingCart} className="stat-icon" />
                    <div className="stat-content">
                        <h3>Buyers</h3>
                        <p className="stat-value">{stats.buyers}</p>
                    </div>
                </div>
                <div className="stat-card">
                    <FontAwesomeIcon icon={faStore} className="stat-icon" />
                    <div className="stat-content">
                        <h3>Sellers</h3>
                        <p className="stat-value">{stats.sellers}</p>
                    </div>
                </div>
                <div className="stat-card">
                    <FontAwesomeIcon icon={faFileAlt} className="stat-icon" />
                    <div className="stat-content">
                        <h3>Total Leads</h3>
                        <p className="stat-value">{stats.leads}</p>
                    </div>
                </div>
            </div>
            
            <div className="recent-leads">
                <h3>Recent Leads</h3>
                {recentLeads.length === 0 ? (
                    <p className="no-data">No leads found. New leads will appear here.</p>
                ) : (
                    <table className="leads-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Type</th>
                                <th>Status</th>
                                <th>Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recentLeads.slice(0, 5).map(lead => (
                                <tr key={lead.id}>
                                    <td>{lead.name}</td>
                                    <td>{lead.email}</td>
                                    <td>{lead.is_buyer ? 'Buyer' : 'Seller'}</td>
                                    <td>
                                        <span className={`status-badge status-${lead.status}`}>
                                            {lead.status}
                                        </span>
                                    </td>
                                    <td>{new Date(lead.created_at).toLocaleDateString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            
            <div className="admin-tiles">
                <div className="admin-tile" onClick={() => window.location.href = '/admin/users'}>
                    <FontAwesomeIcon icon={faUser} />
                    <span>Manage Users</span>
                </div>
                <div className="admin-tile" onClick={() => window.location.href = '/admin/leads'}>
                    <FontAwesomeIcon icon={faChartLine} />
                    <span>Manage Leads</span>
                </div>
                <div className="admin-tile" onClick={() => window.location.href = '/admin/settings'}>
                    <FontAwesomeIcon icon={faBuilding} />
                    <span>Settings</span>
                </div>
                <div className="admin-tile" onClick={() => window.location.href = '/admin/reports'}>
                    <FontAwesomeIcon icon={faFileAlt} />
                    <span>Reports</span>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
